<template>
  <div class="extras-editor">
    <div class="extras-editor__toolbar">
      <v-switch
        v-model="includesExtras"
        inset
        label="Has extras"
        hide-details
        @change="onExtrasChange"
      ></v-switch>
    </div>

    <div v-if="includesExtras" class="extras-editor__content">
      <div class="extras-editor__add">
        <v-select
          :items="availableExtraCategories"
          item-text="name"
          v-model="extraCategoryId"
          item-value="id"
          label="Extra category"
          outlined
          dense
          hide-details="auto"
        />

        <v-btn type="button" color="primary" depressed :disabled="extraCategoryId == null" @click="addExtra()">
          <v-icon left small>mdi-plus</v-icon>
          Add extras
        </v-btn>
      </div>

      <v-text-field
        v-model="extrasSearch"
        prepend-inner-icon="mdi-magnify"
        label="Search selected extras"
        outlined
        dense
        hide-details="auto"
      />

      <div v-if="extras.length === 0" class="extras-editor__empty">
        No extra categories added.
      </div>

      <div v-else class="extra-category-list">
        <v-card
          v-for="extraCategory in visibleExtraCategories"
          :key="extraCategory.id"
          class="extra-category-card"
          outlined
        >
          <div class="extra-category-card__header">
            <v-checkbox
              v-model="extraCategory.selected"
              :label="extraCategory.name"
              hide-details
              @change="selectExtras(extraCategory)"
            ></v-checkbox>

            <v-btn icon color="error" aria-label="Remove extra category" @click="removeCategory(extraCategory)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>

          <div class="extra-category-card__settings">
            <v-text-field
              v-model="extraCategory.noOfFree"
              label="# Of Free"
              type="number"
              outlined
              dense
              hide-details="auto"
            />
            <v-text-field
              v-model="extraCategory.maximum"
              label="Maximum"
              type="number"
              outlined
              dense
              hide-details="auto"
            />
          </div>

          <div class="extra-list">
            <div
              v-for="extra in visibleExtras(extraCategory)"
              :key="extra.id"
              class="extra-item"
              :class="{ 'extra-item--selected': extra.selected }"
            >
              <v-checkbox v-model="extra.selected" :label="extra.name" hide-details></v-checkbox>

              <div class="extra-item__flags">
                <v-checkbox v-model="extra.eposOnly" hide-details label="EPOS" :disabled="!extra.selected"></v-checkbox>
                <v-checkbox v-model="extra.free" hide-details label="Free" :disabled="!extra.selected"></v-checkbox>
                <v-checkbox v-model="extra.hasQty" hide-details label="Qty" :disabled="!extra.selected"></v-checkbox>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <div v-else class="extras-editor__empty">
      No extras enabled.
    </div>
  </div>
</template>

<script>
import { extraCategoryService } from '@/services';

export default {
  props: ['productExtras'],
  async created () {
    this.extraCategoriesList = await extraCategoryService.get(true);
    this.applyProductExtras(this.productExtras);
  },
  data () {
    return {
      extras: [],
      includesExtras: false,
      extraCategoryId: null,
      extraCategoriesList: [],
      extrasSearch: ''
    }
  },
  computed: {
    availableExtraCategories() {
      const selectedIds = this.extras.map(extraCategory => extraCategory.id);
      return this.extraCategoriesList.filter(extraCategory => !selectedIds.includes(extraCategory.id));
    },
    visibleExtraCategories() {
      if (!this.extrasSearch) {
        return this.extras;
      }

      const search = this.extrasSearch.toLowerCase();
      return this.extras.filter(extraCategory => {
        return extraCategory.name.toLowerCase().includes(search) ||
          extraCategory.extras.some(extra => extra.name.toLowerCase().includes(search));
      });
    }
  },
  methods: {
    onExtrasChange: function(enabled) {
      if (!enabled) {
        this.extras = [];
        this.extraCategoryId = null;
        this.extrasSearch = '';
      }
    },
    selectExtras: function(extraCategory) {
      for (var extra of extraCategory.extras) {
        extra.selected = extraCategory.selected;
      }
    },
    removeCategory: function(extraCategory) {
      var idx = this.extras.indexOf(extraCategory);
      if (idx > -1) {
        this.extras.splice(idx, 1);
      }
    },
    getExtras: function() {
      if (!this.includesExtras) {
        return [];
      }

      var productExtras = this.extras.map(extraCategory => ({
        name: extraCategory.name,
        noOfFree: extraCategory.noOfFree == null ? 0 : extraCategory.noOfFree,
        maximum: extraCategory.maximum == null ? 0 : extraCategory.maximum,
        extraCategoryId: extraCategory.id,
        extras: extraCategory.extras.filter(e => e.selected).map(e => ({
          name: e.name,
          description: e.description,
          cost: e.price,
          free: e.free == null ? false : e.free,
          hasQty: e.hasQty == null ? false : e.hasQty,
          eposOnly: e.eposOnly == null ? false : e.eposOnly,
          extraId: e.id
        }))
      }));
      return productExtras;
    },
    hasExtras: function() {
      return this.includesExtras && this.extras.length > 0;
    },
    addExtra: function() {
      if (this.extraCategoryId == null || this.extras.filter(e => e.id == this.extraCategoryId).length > 0)
        return;

      const category = this.extraCategoriesList.find(e => e.id == this.extraCategoryId);
      if (category == null)
        return;

      this.extras.push(this.cloneCategory(category));
      this.extraCategoryId = null;
    },
    applyProductExtras(val) {
      if (!val || val.length === 0 || this.extraCategoriesList.length === 0) {
        if (!val || val.length === 0) {
          this.includesExtras = false;
          this.extras = [];
        }
        return;
      }

      this.includesExtras = true;
      this.extras = [];

      for (let extraCategory of this.extraCategoriesList) {
        var productExtraCategory = val.find(e => e.extraCategoryId == extraCategory.id);
        if (productExtraCategory == null)
          continue;

        const category = this.cloneCategory(extraCategory);
        category.noOfFree = productExtraCategory.noOfFree;
        category.maximum = productExtraCategory.maximum;

        for (let extra of category.extras) {
          var productExtra = productExtraCategory.extras.find(e => e.extraId == extra.id);
          if (productExtra == null)
            continue;

          extra.eposOnly = productExtra.eposOnly;
          extra.free = productExtra.free;
          extra.hasQty = productExtra.hasQty;
          extra.selected = true;
        }

        category.selected = category.extras.length > 0 && category.extras.every(extra => extra.selected);
        this.extras.push(category);
      }
    },
    cloneCategory(category) {
      const clone = JSON.parse(JSON.stringify(category));
      clone.selected = clone.selected || false;
      clone.noOfFree = clone.noOfFree == null ? 0 : clone.noOfFree;
      clone.maximum = clone.maximum == null ? 0 : clone.maximum;
      clone.extras = (clone.extras || []).map(extra => ({
        ...extra,
        selected: extra.selected || false,
        eposOnly: extra.eposOnly || false,
        free: extra.free || false,
        hasQty: extra.hasQty || false
      }));
      return clone;
    },
    visibleExtras(extraCategory) {
      if (!this.extrasSearch) {
        return extraCategory.extras;
      }

      const search = this.extrasSearch.toLowerCase();
      if (extraCategory.name.toLowerCase().includes(search)) {
        return extraCategory.extras;
      }

      return extraCategory.extras.filter(extra => extra.name.toLowerCase().includes(search));
    }
  },
  watch: {
    productExtras: function(val) {
      this.applyProductExtras(val);
    }
  }
}
</script>

<style lang="scss" scoped>
.extras-editor {
  display: grid;
  gap: 12px;
}

.extras-editor__toolbar {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  display: flex;
  min-height: 58px;
  padding: 4px 12px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
    width: 100%;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }
}

.extras-editor__content {
  display: grid;
  gap: 12px;
}

.extras-editor__add {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
}

.extras-editor__empty {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  padding: 18px;
  text-align: center;
}

.extra-category-list {
  display: grid;
  gap: 12px;
}

.extra-category-card {
  padding: 14px;
}

.extra-category-card__header {
  align-items: center;
  border-bottom: 1px solid #dfe7ef;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }
}

.extra-category-card__settings {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
  margin-bottom: 12px;
}

.extra-list {
  display: grid;
  gap: 8px;
}

.extra-item {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  display: grid;
  gap: 8px;
  padding: 8px 10px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }
}

.extra-item--selected {
  background: #effaf8;
  border-color: #9ad8ce;
}

.extra-item__flags {
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (min-width: 720px) {
  .extras-editor__add {
    align-items: center;
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .extra-category-card__settings {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .extra-item {
    align-items: center;
    grid-template-columns: minmax(0, 1fr) 280px;
  }
}
</style>
